"use client";

// ============================================================
// CLUBHUB – Nachrichten: das Textfeld mit Werkzeugleiste
// ============================================================
// Ein Textfeld, darueber eine kleine Leiste (fett, kursiv, Verweis,
// Listen, Zwischenueberschrift) und die Platzhalter als Knoepfe.
// Gespeichert wird Markdown — wer es kann, tippt es; wer nicht,
// nimmt die Leiste. Die Vorschau daneben zeigt das Ergebnis.
//
// Fuer SMS gibt es keine Auszeichnung: dort steht nur die Reihe der
// Platzhalter ueber dem Feld.
//
// DAS FELD WAECHST MIT DEM TEXT, statt innen zu rollen — der Text
// steht dann Zeile fuer Zeile neben seiner Vorschau. Steht es in
// einer hoeheren Spalte (Editor neben der Vorschau), fuellt es den
// Platz bis unten: Gemessen wird ohne Dehnung, die Messung wird
// Mindesthoehe, den Rest gibt das Flex-Layout dazu.
// ============================================================
import { useLayoutEffect, useRef } from "react";
import { Bold, Braces, Heading2, Italic, Link2, List, ListOrdered } from "lucide-react";
import { cn } from "@/lib/utils";
import { PLATZHALTER } from "@/lib/nachrichten/platzhalter";

type Werkzeugart = "fett" | "kursiv" | "verweis" | "liste" | "nummern" | "ueberschrift";

const WERKZEUGE: { art: Werkzeugart; label: string; kurz?: string; icon: typeof Bold }[] = [
  { art: "fett", label: "Fett", kurz: "⌘B", icon: Bold },
  { art: "kursiv", label: "Kursiv", kurz: "⌘I", icon: Italic },
  { art: "verweis", label: "Verweis", kurz: "⌘K", icon: Link2 },
  { art: "liste", label: "Liste", icon: List },
  { art: "nummern", label: "Nummerierte Liste", icon: ListOrdered },
  { art: "ueberschrift", label: "Zwischenüberschrift", icon: Heading2 },
];

export function MarkdownFeld({
  id,
  value,
  onChange,
  fehler,
  sms = false,
  zeilen = 12,
  platzhalterText,
  className,
}: {
  id: string;
  value: string;
  onChange: (text: string) => void;
  fehler?: string;
  sms?: boolean;
  zeilen?: number;
  platzhalterText?: string;
  className?: string;
}) {
  const feld = useRef<HTMLTextAreaElement>(null);

  // Vor dem Zeichnen messen, sonst springt das Feld fuer ein Bild.
  useLayoutEffect(() => {
    const el = feld.current;
    if (!el) return;
    const messen = () => {
      el.style.flex = "none";
      el.style.minHeight = "0px";
      el.style.height = "auto";
      const hoehe = el.scrollHeight;
      el.style.flex = "";
      el.style.height = "";
      el.style.minHeight = `${hoehe}px`;
    };
    messen();
    // Andere Breite, andere Umbrueche: neu messen.
    let breite = el.clientWidth;
    const beobachter = new ResizeObserver(() => {
      if (el.clientWidth === breite) return;
      breite = el.clientWidth;
      messen();
    });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [value, sms, zeilen]);

  function setzen(neu: string, von: number, bis: number) {
    onChange(neu);
    requestAnimationFrame(() => {
      const el = feld.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(von, bis);
    });
  }

  function umschliessen(vor: string, nach: string, ersatz: string) {
    const el = feld.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const auswahl = value.slice(a, b) || ersatz;
    setzen(
      value.slice(0, a) + vor + auswahl + nach + value.slice(b),
      a + vor.length,
      a + vor.length + auswahl.length,
    );
  }

  function zeilenVorsatz(vorsatz: (i: number) => string) {
    const el = feld.current;
    if (!el) return;
    const a = value.lastIndexOf("\n", el.selectionStart - 1) + 1;
    const endeZeile = value.indexOf("\n", el.selectionEnd);
    const b = endeZeile === -1 ? value.length : endeZeile;
    const block = value.slice(a, b) || "Eintrag";
    const neu = block
      .split("\n")
      .map((z, i) => `${vorsatz(i)}${z.replace(/^(\s*[-*]\s+|\s*\d+[.)]\s+|#{1,3}\s+)/, "")}`)
      .join("\n");
    // Eine Liste braucht eine Leerzeile davor, sonst klebt sie am Absatz.
    const davor = a > 0 && value[a - 2] !== "\n" && value[a - 1] === "\n" ? "\n" : "";
    setzen(
      value.slice(0, a) + davor + neu + value.slice(b),
      a + davor.length,
      a + davor.length + neu.length,
    );
  }

  function verweis() {
    const el = feld.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const text = value.slice(a, b) || "Verweistext";
    const roh = `[${text}](https://)`;
    const start = a + text.length + 3;
    setzen(value.slice(0, a) + roh + value.slice(b), start, start + 8);
  }

  function einfuegen(text: string) {
    const el = feld.current;
    const a = el?.selectionStart ?? value.length;
    const b = el?.selectionEnd ?? value.length;
    setzen(value.slice(0, a) + text + value.slice(b), a + text.length, a + text.length);
  }

  function ausfuehren(art: Werkzeugart) {
    if (art === "fett") umschliessen("**", "**", "fett");
    else if (art === "kursiv") umschliessen("*", "*", "kursiv");
    else if (art === "verweis") verweis();
    else if (art === "liste") zeilenVorsatz(() => "- ");
    else if (art === "nummern") zeilenVorsatz((i) => `${i + 1}. `);
    else zeilenVorsatz(() => "## ");
  }

  function tasten(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (sms || !(e.metaKey || e.ctrlKey)) return;
    const art = ({ b: "fett", i: "kursiv", k: "verweis" } as const)[
      e.key.toLowerCase() as "b" | "i" | "k"
    ];
    if (!art) return;
    e.preventDefault();
    ausfuehren(art);
  }

  return (
    <div
      className={cn(
        "bg-bg-primary flex flex-col overflow-hidden rounded-lg border",
        "duration-fast ease-standard transition-[box-shadow,border-color] focus-within:ring-2",
        fehler
          ? "border-danger focus-within:ring-danger/30"
          : "border-border focus-within:border-accent/40 focus-within:ring-accent/30",
        className,
      )}
    >
      <div className="border-border-subtle bg-bg-secondary/60 flex flex-wrap items-center gap-0.5 border-b px-1.5 py-1">
        {!sms && (
          <>
            {WERKZEUGE.map((w) => (
              <button
                key={w.art}
                type="button"
                onClick={() => ausfuehren(w.art)}
                title={w.kurz ? `${w.label} (${w.kurz})` : w.label}
                aria-label={w.label}
                className="text-text-secondary hover:bg-bg-tertiary hover:text-text-primary grid h-8 w-8 place-items-center rounded-md transition-colors"
              >
                <w.icon className="h-4 w-4" aria-hidden />
              </button>
            ))}
            <span className="bg-border mx-1 h-4 w-px" aria-hidden />
          </>
        )}
        <span
          className="text-caption text-text-tertiary flex items-center gap-1 px-1.5"
          title="Wird je Empfänger ersetzt"
        >
          <Braces className="h-3.5 w-3.5" aria-hidden />
          <span className="max-sm:sr-only">Platzhalter</span>
        </span>
        <div className="flex min-w-0 flex-1 [scrollbar-width:none] gap-1 overflow-x-auto py-0.5">
          {PLATZHALTER.map((p) => (
            <button
              key={p.schluessel}
              type="button"
              onClick={() => einfuegen(`{{${p.schluessel}}}`)}
              title={`{{${p.schluessel}}} — ${p.hinweis}`}
              className="bg-bg-primary text-caption text-text-secondary ring-border-subtle hover:text-text-primary hover:ring-border shrink-0 rounded-md px-2 py-1 ring-1 transition-colors ring-inset"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <textarea
        ref={feld}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={tasten}
        rows={sms ? 5 : zeilen}
        placeholder={platzhalterText}
        aria-invalid={fehler ? true : undefined}
        className={cn(
          "text-body-md text-text-primary placeholder:text-text-tertiary block w-full flex-1 resize-none overflow-y-auto bg-transparent px-3.5 py-3 outline-none",
          !sms && "text-body-sm font-mono leading-relaxed",
        )}
      />
    </div>
  );
}
